import { divided, minus, plus, sizedRectangle } from "@/math/coordinates";
import type {
  BoardCoordinate,
  SizedRectangle,
  ViewportCoordinate,
  WindowCoordinate,
  WindowLen,
} from "@/model/coordinates";
import { windowLen } from "@/model/coordinates";

export function scrollPos(): WindowLen {
  return windowLen(window.scrollX, window.scrollY);
}

export function viewportFromWindow(c: WindowCoordinate): ViewportCoordinate {
  return minus(c, scrollPos()) as unknown as ViewportCoordinate;
}

export function windowFromViewport(c: ViewportCoordinate): WindowCoordinate {
  return plus(c, scrollPos()) as unknown as WindowCoordinate;
}

export function centerCoord(): ViewportCoordinate {
  return divided(rawInnerSize(), 2);
}

// complete window size
export function windowSize(): WindowLen {
  return rawInnerSize();
}

function rawInnerSize<T extends ViewportCoordinate | WindowCoordinate | WindowLen>(): T {
  return { x: window.innerWidth, y: window.innerHeight } as T;
}

// window size without scroll bars
export function contentSize(): WindowLen {
  return rawClientSize(document.documentElement);
}

export function clientSize(el: Element): WindowLen {
  return rawClientSize(el);
}

function rawClientSize<T extends WindowCoordinate | WindowLen>(el: Element): T {
  return { x: el.clientWidth, y: el.clientHeight } as T;
}

export function scrollSize(el: Element): WindowLen {
  return windowLen(el.scrollWidth, el.scrollHeight);
}

export function elemScrollPos(el: Element): WindowLen {
  return windowLen(el.scrollLeft, el.scrollTop);
}

export function offsetSize(el: HTMLElement): WindowLen {
  return windowLen(el.offsetWidth, el.offsetHeight);
}

// the caller must know if el is in window or board coordinates
export function elemOffsetCoord<T extends WindowCoordinate | BoardCoordinate>(el: HTMLElement): T {
  return { x: el.offsetLeft, y: el.offsetTop } as T;
}

export function boundsCoord(
  elem: HTMLElement | { getBoundingClientRect: () => DOMRect },
): ViewportCoordinate {
  const { left, top } = elem.getBoundingClientRect();
  return viewportCoord(left, top);
}

export function boundsRect(
  elem: HTMLElement | { getBoundingClientRect: () => DOMRect },
): SizedRectangle<ViewportCoordinate> {
  const { left, top, width, height } = elem.getBoundingClientRect();
  return sizedRectangle({ from: viewportCoord(left, top), size: viewportCoord(width, height) });
}

export function clientCoord(e: MouseEvent): ViewportCoordinate {
  return viewportCoord(e.clientX, e.clientY);
}

/**
 * Determines whether the given position is inside the bounding rectangle of the parent element
 */
export function isInsideParent(pos: ViewportCoordinate, parent: HTMLElement): boolean {
  const rect = boundsRect(parent);
  return (
    pos.x >= rect.p0.x &&
    pos.x <= rect.p0.x + rect.size.x &&
    pos.y >= rect.p0.y &&
    pos.y <= rect.p0.y + rect.size.y
  );
}

export function isInsideCoords(
  el: HTMLElement,
  from: ViewportCoordinate,
  to: ViewportCoordinate,
): boolean {
  const rect = boundsRect(el);
  return (
    rect.p0.x >= from.x &&
    rect.p0.x + rect.size.x <= to.x &&
    rect.p0.y >= from.y &&
    rect.p0.y + rect.size.y <= to.y
  );
}

// here is the only place allowed to create window/viewport coordinates
function viewportCoord(x: number, y: number): ViewportCoordinate {
  return { x, y } as ViewportCoordinate;
}
